import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";

import { SiChatbot } from "react-icons/si";
import { MdLocalPhone, MdOutlineMail } from "react-icons/md";
import { IoMdSend } from "react-icons/io";
import appstore from "../store/appStore";
import { CloseIcon } from "@chakra-ui/icons";
import { motion, AnimatePresence } from "framer-motion";
import { DragPoweredBy } from "./components/DragPowered";
import { getSessionId } from "../client/sessionSetup";

const DraggableBox = ({
  height,
  botDetails,
  rufusQue,
  iframeSrc,
  isColorDark,
  conversationStart,
  boxHeight,
  setBoxHeight,
}) => {
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const mobileHeight = window.innerHeight;
  const [rufusText, setRufusText] = useState("");
  const [isScrolling, setIsScrolling] = useState(true);
  // To toggle auto-scrolling
  const [switchSize, setSwitchSize] = useState(false);
  const scrollContainerRef = useRef(null);
  const startBoxHeightRef = useRef(75);
  const dragOffsetYRef = useRef(0);
  const adjustColor = (hex, lightnessBoost = 15, saturationBoost = 10) => {
    if (!hex) return null;
  
    // Remove # if present
    let usePound = hex.startsWith("#");
    hex = hex.replace("#", "");
  
    // Convert hex to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
  
    // Convert RGB to HSL
    let { h, s, l } = rgbToHsl(r, g, b);
  
    // Increase saturation and lightness for the shine effect
    l = Math.min(100, l + lightnessBoost);
    s = Math.min(100, s + saturationBoost);
  
    // Convert HSL back to RGB
    let { r: newR, g: newG, b: newB } = hslToRgb(h, s, l);
  
    // Convert back to hex
    let newHex = `#${((1 << 24) | (newR << 16) | (newG << 8) | newB).toString(16).slice(1)}`;
  
    return usePound ? newHex : newHex.slice(1);
  };
  
  // Convert RGB to HSL
  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
  
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
  
    if (max === min) {
      h = s = 0; // achromatic
    } else {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h *= 60;
    }
    return { h, s: s * 100, l: l * 100 };
  };
  
  // Convert HSL back to RGB
  const hslToRgb = (h, s, l) => {
    s /= 100;
    l /= 100;
  
    let c = (1 - Math.abs(2 * l - 1)) * s;
    let x = c * (1 - Math.abs((h / 60) % 2 - 1));
    let m = l - c / 2;
    let r, g, b;
  
    if (h < 60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
  
    return { 
      r: Math.round((r + m) * 255), 
      g: Math.round((g + m) * 255), 
      b: Math.round((b + m) * 255) 
    };
  };
  
  
  // Start automatic scrolling
  useEffect(() => {
    let scrollInterval;

    if (isScrolling) {
      scrollInterval = setInterval(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft += 2; // Adjust speed as needed
        }
      }, 50); // Adjust frequency as needed
    }

    return () => clearInterval(scrollInterval);
  }, [isScrolling]);

  useEffect(() => {
    setTimeout(() => {
      setSwitchSize(true);
    }, 100);
  }, []);

  // Handle user touch to stop scrolling
  const handleTouchStart = (e) => {
    // Avoid stopping scrolling when touching the container
    setIsScrolling(false);
    e.stopPropagation();
  };

  const handleTouchEnd = (e) => {
    setIsScrolling(true);
    e.stopPropagation();
  };

  const updateDragPosition = () => {
    if (draggingRef.current) {
      requestAnimationFrame(updateDragPosition);
    }
  };

  // Start dragging
  const startDrag = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    startYRef.current = e.touches[0].clientY; // Capture the initial touch position
    startBoxHeightRef.current = boxHeight; // Store the current height of the box
    dragOffsetYRef.current = 0; // Reset vertical drag offset
    requestAnimationFrame(updateDragPosition);
  };

  // During dragging
  const duringDrag = (e) => {
    if (!draggingRef.current) return;

    const currentY = e.touches[0].clientY; // Get current touch position
    const diffY = currentY - startYRef.current; // Calculate the difference in Y-position

    // Calculate new height based on the initial box height and the difference in touch position
    const newHeight = Math.max(
      30,
      Math.min(mobileHeight - 65, startBoxHeightRef.current - diffY)
    );

    // Update the box height smoothly
    setBoxHeight(newHeight);
  };

  // End dragging
  const endDrag = () => {
    draggingRef.current = false;

    if (boxHeight <= 30) {
      setBoxHeight(30); // Snap to 20px if dragged close to the minimum height
    } else if (boxHeight < mobileHeight / 2) {
      if (boxHeight < mobileHeight / 4) {
        setBoxHeight(75); // Snap to 100px
      } else {
        setBoxHeight(mobileHeight / 2); // Snap to half the screen height
      }
    } else {
      setBoxHeight(mobileHeight - 65); // Snap to the full open height
    }
  };

  useEffect(() => {
    if (boxHeight > 75) {
      const chatbotFrame = document.getElementById("chatbot-iframe"); // Get iframe by ID
      if (chatbotFrame)
        chatbotFrame.contentWindow.postMessage(
          { type: "askii", askii: true },
          "*"
        );
    } else {
      const chatbotFrame = document.getElementById("chatbot-iframe"); // Get iframe by ID
      if (chatbotFrame)
        chatbotFrame.contentWindow.postMessage(
          { type: "askii", askii: false },
          "*"
        );
    }
    const draggableElement = document.querySelector("#draggable-box");

    if (draggableElement) {
      draggableElement.addEventListener("touchstart", startDrag, {
        passive: false,
      });
      draggableElement.addEventListener("touchmove", duringDrag, {
        passive: false,
      });
      draggableElement.addEventListener("touchend", endDrag);

      return () => {
        draggableElement.removeEventListener("touchstart", startDrag);
        draggableElement.removeEventListener("touchmove", duringDrag);
        draggableElement.removeEventListener("touchend", endDrag);
      };
    }
  }, [boxHeight]);

  return (
    <Box
      position="fixed"
      bottom={0}
      left="0"
      height={!switchSize ? `${height}px` : `${boxHeight}px`}
      width="100%"
      boxShadow={
        boxHeight == 30 ? "none" : "rgba(149, 157, 165, 0.2) 0px 8px 24px"
      }
      paddingTop={2}
      paddingBottom={2}
      display="flex"
      flexDirection="column"
      alignItems="center"
      zIndex={2147483647}
      style={{
        transition: draggingRef.current ? "none" : "height 0.3s ease-in-out",
      }}
      borderTopLeftRadius="15px"
      borderTopRightRadius="15px"
      backgroundColor={boxHeight == 30 ? "transparent" : "white"}
      borderTop={boxHeight == 30 ? "none" : "1px solid 		#DCDCDC"}
    >
      {/* Drag Handle */}
      <Box
        width="100%"
        height={`${boxHeight <= 75 ? "20px" : "35px"}`}
        id="draggable-box"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderTopLeftRadius="15px"
        borderTopRightRadius="15px"
        backgroundColor={boxHeight == 30 ? "transparent" : "white"}
        pb={"6px"}
        borderBottom={boxHeight == 30 ? "none" : "1px solid 		#DCDCDC"}
      >
        <div style={{ width: "60%", display: "flex", justifyContent: "right" }}>
          <div>
            <Box width="70px" height="8px" bg="gray" borderRadius="20px">
              {" "}
            </Box>

            {boxHeight > 75 && (
              <div style={{ display: "flex", marginTop: 4 }}>
                <p style={{ fontWeight: 700 }}>
                  {/* Animate "Askii" */}
                  {["A", "s", "k", "i", "i"].map((letter, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.5 }}
                    >
                      {letter}
                    </motion.span>
                  ))}
                </p>

                {/* Animate "PoweredBy" after "Askii" */}
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, y: 2 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 0.5 }} // Delay to wait for "Askii" animation
                  >
                    {/* <DragPoweredBy /> */}
                  </motion.div>
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        <div style={{ width: "30%", display: "flex", justifyContent: "right" }}>
          {boxHeight !== 30 ? (
            <CloseIcon
              onTouchStart={() => {
                setBoxHeight(30);
              }}
              right={0}
              top={0}
              zIndex={2147483647}
              style={{
                width: "10px",
                height: "10px",
                color: isColorDark(botDetails.accentColor) ? "white" : "black",
              }}
              cursor="pointer"
            />
          ) : null}
        </div>
      </Box>

      <iframe
        style={{ display: boxHeight > 75 ? "block" : "none" }}
        id="chatbot-iframe"
        src={iframeSrc}
        width={"100%"}
        height={"100%"}
      ></iframe>

      {/* Rufus Queue */}

      {!draggingRef.current && boxHeight == 75 ? (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={2}
          overflowX="scroll"
          overflowY={"hidden"}
          padding="2"
          paddingLeft={4}
          paddingRight={4}
          borderColor="gray.500"
          width="100%"
          css={{
            "::-webkit-scrollbar": {
              height: "6px",
            },
            "::-webkit-scrollbar-thumb": {
              background: "gray.400",
              borderRadius: "3px",
            },
            "::-webkit-scrollbar-track": {
              background: "transparent",
            },
            scrollBehavior: "smooth", // Smooth scrolling
          }}
          // ref={scrollContainerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {rufusQue?.map((ele, i) => (
            <Box
            key={i}
            className="box"
            style={{
              whiteSpace: "nowrap",
              backgroundColor: botDetails?.accentColor,
              fontSize: "12px",
              color:isColorDark(botDetails?.accentColor)?"white":"black",
              padding: 4,
              "--main-bg": `conic-gradient(
                from var(--border-angle),
                ${botDetails?.accentColor || "#213"},
                ${botDetails?.accentColor || "#112"} 5%,
                ${botDetails?.accentColor || "#112"} 60%,
                ${botDetails?.accentColor || "#213"} 95%
              )`,
              "--gradient-border": `conic-gradient(
                from var(--border-angle),
                transparent 25%,
                ${adjustColor(botDetails?.accentColor, 40) || "#08f"},
                ${adjustColor(botDetails?.accentColor, -40) || "#f03"} 99%,
                transparent
              )`,
            }}
            onClick={() => {
              setBoxHeight(window.innerHeight - 65);
              if (
                botDetails.askDetailsBeforeChat &&
                !conversationStart.isStart &&
                (botDetails.askEmail || botDetails.askPhone)
              ) {
                const chatbotFrame =
                  document.getElementById("chatbot-iframe"); // Get iframe by ID
                if (chatbotFrame)
                  chatbotFrame.contentWindow.postMessage(
                    { type: "rufusTextKnow", rufusText: ele, askii: true ,productId:window.meta?.product?.id},
                    "*"
                  );
                // openChatBot();
              } else {
                const chatbotFrame =
                  document.getElementById("chatbot-iframe"); // Get iframe by ID
                if (chatbotFrame) {
                  chatbotFrame.contentWindow.postMessage(
                    { type: "rufusText", rufusText: ele, askii: true ,productId:window.meta?.product?.id},
                    "*"
                  );
                }
                // sendMessageToBot(event, ele);
                // openChatBot();
                // setSuggestions(null);
              }
              // openChatBot()
            }}
          >
            {ele}
          </Box>

  
        
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default DraggableBox;
